import { useMemo, useState } from "react";
import { FileDown, Eye } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import { fetchAPIBlob } from "../../config/api";
import { ReportHeader } from "../../components/reports";
import { BaseballTable } from "../../components/tables/BaseballTable";
import {
  getSplitsBatterColumns,
  getSplitsPitcherColumns,
} from "../../config/leaderboards";
import { SPLITS_KINDS } from "./components/splits-field-config";
import { useSplitsReportData } from "./hooks/use-splits-report-data";

const SESSION_KEY = "splitsReportConfig";

export function SplitsReportPage({ kind }) {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { report, filteredRows, loading } = useSplitsReportData(reportId, kind);
  const [exporting, setExporting] = useState(false);

  const meta = SPLITS_KINDS[kind];

  const columns = useMemo(() => {
    const full = kind === "pitcher" ? getSplitsPitcherColumns : getSplitsBatterColumns;
    return full.filter((c) => c.name !== "#");
  }, [kind]);

  const defaultSortField = useMemo(() => {
    const name = kind === "pitcher" ? "o-wOBA" : "wOBA";
    const idx = columns.findIndex((c) => c.name === name);
    return idx >= 0 ? idx + 1 : 1;
  }, [columns, kind]);

  const exportPayload = useMemo(
    () =>
      report
        ? {
            kind,
            report: {
              teamName: report.teamName,
              teamId: report.teamId,
              year: report.year,
            },
            rows: filteredRows,
          }
        : null,
    [kind, report, filteredRows]
  );

  const handlePreview = () => {
    if (!exportPayload?.rows?.length) return;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(exportPayload));
    window.open("/render/splits-report", "_blank");
  };

  const handleExport = async () => {
    if (exporting || !exportPayload?.rows?.length) return;
    setExporting(true);
    try {
      const blob = await fetchAPIBlob("/reports/splits_report", {
        method: "POST",
        body: JSON.stringify(exportPayload),
      });
      saveAs(
        blob,
        `${report.teamId || "team"}_${meta.fileSlug}_${report.year}.pdf`
      );
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Report Not Found</h2>
          <button
            onClick={() => navigate("/scouting")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Scouting
          </button>
        </div>
      </div>
    );
  }

  const csvName = `${(report.teamName || "team").replace(/\s+/g, "_")}_${meta.fileSlug}_${report.year}.csv`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <ReportHeader
          teamId={report.teamId}
          teamName={report.teamName}
          reportType={meta.reportType}
          year={report.year}
        >
          <button
            onClick={handlePreview}
            disabled={filteredRows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Eye size={14} />
            Preview
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || filteredRows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown size={14} />
            {exporting ? "Exporting…" : "Export PDF"}
          </button>
        </ReportHeader>

        <BaseballTable
          data={filteredRows}
          columns={columns}
          filename={csvName}
          defaultSortField={defaultSortField}
        />
      </div>
    </div>
  );
}
